// 推荐理由 — the short paragraph the member reads next to a recommended person
// on the 确认推荐 link. The system drafts it from the two 速记卡, the teacher
// edits it and ticks 确认; only confirmed text is ever shown to the member.
//
// The draft only uses fields that are fine for a member to read:
// 核心优势 / 最适合类型 / 爱的语言. 潜在顾虑, 绝对底线, 离婚原因 and the rest of
// the card are teacher-only and never go into it.
import React from 'react';
import { Wand2 } from 'lucide-react';
import { L } from '../../../theme/luxe';

const clean = (s) => String(s == null ? '' : s).trim().replace(/[。；;，,\s]+$/, '');

// 「接受礼物 2 · 服务的行动 1（主导：接受礼物）」 → 接受礼物; free text stays as is.
export const dominantLove = (s) => {
  const v = clean(s);
  if (!v) return '';
  const m = v.match(/主导[：:]\s*([^）)]+)/);
  return (m ? m[1] : v).trim();
};

export function draftRecommendReason({ theirName = '对方', mine = {}, theirs = {} } = {}) {
  const lines = [];
  const strengths = clean(theirs.strengths);
  if (strengths) lines.push(`${theirName}的优势：${strengths}。`);
  const want = clean(mine.best_match_type);
  if (want) lines.push(`老师为你挑选的方向：${want}。`);
  const a = dominantLove(mine.love_language);
  const b = dominantLove(theirs.love_language);
  if (a && b && a === b) lines.push(`你们的主导爱的语言都是「${a}」，相处时更容易感受到彼此的心意。`);
  else if (a && b) lines.push(`你的主导爱的语言是「${a}」，${theirName}是「${b}」。了解彼此表达爱的方式，相处会更顺。`);
  else if (b) lines.push(`${theirName}的主导爱的语言是「${b}」。`);
  return lines.join('\n');
}

const C = { ink: '#111111', muted: '#6E685F', line: 'rgba(17,17,17,.12)', ok: '#3F7D5A', warn: '#93591A', paper: '#FFFDF9', serif: L.serif };

// value: { text, confirmed }. onChange gets the next value. Editing the text
// clears 确认 — the teacher confirms what the member will actually read.
export default function RecommendReasonEditor({ name, value, onChange, draft = '', isZh = true, disabled = false }) {
  const t = (zh, en) => (isZh ? zh : en);
  const text = value && value.text != null ? value.text : '';
  const confirmed = !!(value && value.confirmed && text.trim());
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: C.serif, fontSize: 15, fontWeight: 500, color: C.ink, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        {draft && (
          <button type="button" disabled={disabled} onClick={() => onChange({ text: draft, confirmed: false })}
            title={t('用双方速记卡（核心优势 / 最适合类型 / 爱的语言）重新写草稿', 'Redraft from both quick cards')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 400, letterSpacing: '.14em', padding: '3px 8px', borderRadius: 2, border: `1px solid ${C.line}`, background: 'transparent', color: C.ink, cursor: 'pointer', fontFamily: 'inherit' }}>
            <Wand2 size={11} strokeWidth={1.5} />{t('重新生成草稿', 'Redraft')}
          </button>
        )}
      </div>
      <textarea value={text} disabled={disabled} rows={3} maxLength={2000}
        onChange={(e) => onChange({ text: e.target.value, confirmed: false })}
        placeholder={t('写给会员看的推荐理由（速记卡没有可用内容时请手写）', 'Why this person — the member will read this')}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12.5, lineHeight: 1.6, padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 2, background: C.paper, fontFamily: 'inherit', resize: 'vertical' }} />
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 400, letterSpacing: '.06em', marginTop: 4, cursor: text.trim() ? 'pointer' : 'not-allowed', color: confirmed ? C.ok : C.warn }}>
        <input type="checkbox" checked={confirmed} disabled={disabled || !text.trim()}
          onChange={(e) => onChange({ text, confirmed: e.target.checked })} />
        {confirmed ? t('已确认，会员在确认链接里看得到', 'Confirmed — shown to the member')
          : t('未确认，会员看不到（核对后打勾）', 'Not confirmed — hidden from the member')}
      </label>
    </div>
  );
}
